import { Box, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { jsonDiffTableStyles as styles } from './json-diff-table.styles'

const SKIP_FIELDS = new Set(['customFields', 'tags', 'locationId'])

function formatVal(v: unknown): string {
  if (v === null || v === undefined) return '—'
  if (typeof v === 'boolean') return v ? 'Yes' : 'No'
  if (v === 'true') return 'Yes'
  if (v === 'false') return 'No'
  if (v === '') return '(empty)'
  return String(v)
}

interface DiffRow {
  field: string
  from: string
  to: string
}

function buildDiffRows(
  oldObj: Record<string, unknown>,
  newObj: Record<string, unknown>,
  tField: (key: string) => string,
  tDndLabel: (channel: string) => string
): DiffRow[] {
  const rows: DiffRow[] = []
  const allKeys = new Set([...Object.keys(oldObj), ...Object.keys(newObj)])

  for (const key of allKeys) {
    if (SKIP_FIELDS.has(key)) continue
    // Skip fields that only exist in old — these are internal fields absent from the new payload,
    // not actual deletions (e.g. Hapana-specific fields stripped before sending to GHL)
    if (!(key in newObj)) continue

    const oldVal = oldObj[key]
    const newVal = newObj[key]

    if (key === 'dndSettings') {
      const oldDnd = (oldVal ?? {}) as Record<string, any>
      const newDnd = (newVal ?? {}) as Record<string, any>
      const channels = new Set([...Object.keys(oldDnd), ...Object.keys(newDnd)])
      for (const channel of channels) {
        const from = oldDnd[channel]?.status ?? '—'
        const to = newDnd[channel]?.status ?? '—'
        if (from !== to) {
          rows.push({ field: tDndLabel(channel), from: formatVal(from), to: formatVal(to) })
        }
      }
      continue
    }

    const fromStr = typeof oldVal === 'object' ? JSON.stringify(oldVal) : formatVal(oldVal)
    const toStr = typeof newVal === 'object' ? JSON.stringify(newVal) : formatVal(newVal)

    if (fromStr !== toStr) {
      rows.push({ field: tField(key), from: fromStr, to: toStr })
    }
  }

  return rows
}

export function tryParseJson(v: unknown): Record<string, unknown> | null {
  if (typeof v !== 'string') return null
  const trimmed = v.trim()
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return null
  try {
    const parsed = JSON.parse(trimmed)
    return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed) ? parsed : null
  } catch {
    return null
  }
}

interface JsonDiffTableProps {
  oldValue: string | null
  newValue: string | null
}

export const JsonDiffTable = ({ oldValue, newValue }: JsonDiffTableProps) => {
  const { t } = useTranslation('common')

  const tField = (key: string) =>
    t(`auditDiffTable.fields.${key}`, {
      defaultValue: key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()),
    })

  const tDndLabel = (channel: string) =>
    t('auditDiffTable.fields.dndLabel', { channel, defaultValue: `DND — ${channel}` })

  const oldObj = tryParseJson(oldValue)
  const newObj = tryParseJson(newValue)

  if (!oldObj && !newObj) return null

  const rows = buildDiffRows(oldObj ?? {}, newObj ?? {}, tField, tDndLabel)

  if (rows.length === 0) {
    return <Typography sx={styles.noChanges}>{t('auditDiffTable.noChanges')}</Typography>
  }

  return (
    <Box component="table" sx={styles.table}>
      <Box component="thead">
        <Box component="tr">
          <Box
            component="th"
            sx={{
              ...styles.colHeader,
              ...styles.cell,
              width: styles.fieldColWidth,
              textAlign: 'left',
            }}
          >
            {t('auditDiffTable.field')}
          </Box>
          <Box
            component="th"
            sx={{
              ...styles.colHeader,
              ...styles.cell,
              width: styles.valueColWidth,
              textAlign: 'left',
            }}
          >
            {t('auditDiffTable.from')}
          </Box>
          <Box
            component="th"
            sx={{
              ...styles.colHeader,
              ...styles.cell,
              width: styles.valueColWidth,
              textAlign: 'left',
            }}
          >
            {t('auditDiffTable.to')}
          </Box>
        </Box>
      </Box>
      <Box component="tbody">
        {rows.map((row, i) => (
          <Box component="tr" key={i} sx={styles.lastRow}>
            <Box component="td" sx={{ ...styles.cell, ...styles.fieldCell }}>
              {row.field}
            </Box>
            <Box component="td" sx={{ ...styles.cell, ...styles.fromCell }}>
              {row.from}
            </Box>
            <Box component="td" sx={{ ...styles.cell, ...styles.toCell }}>
              {row.to}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  )
}
